import { FocusZoneDirection } from "@fluentui/react/lib/FocusZone";
import {
  assertNever,
  findIndex,
  memoizeFunction,
  replaceElement,
} from "@fluentui/react/lib/Utilities";

import type { AppFlyoutAnchorProps } from "@1js/acui-button";
import { assert } from "@1js/acui-common";
import type {
  ControlInMenuRendererFunction,
  MenuDefinition,
} from "@1js/acui-menu";
import type { WindowMode } from "@1js/window-util";
import { getWindowMode } from "@1js/window-util";

import type { ControlRendererFunction } from "../../../controls";
import type {
  ConvertGroupToFlyout,
  MoveToOverflow,
  RibbonScalingStep,
  RibbonScalingTransformation,
} from "../../../Ribbon/Scaling";
import type {
  RibbonControlProps,
  RibbonPreviewGalleryPropsUnion,
} from "../../Controls/RibbonControlProps";
import { getMLRControlGroupMenuClassNames } from "../../getMLRMenuClassNames";
import type {
  RibbonControlGroupDefinition,
  RibbonControlGroupDefinitionMLR,
  RibbonControlGroupDefinitionSet,
  RibbonControlGroupDefinitionSetMLR,
  RibbonControlGroupDefinitionSetSLR,
  RibbonControlGroupDefinitionSLR,
  RibbonMode,
} from "../Ribbon/Ribbon.Props";
import type { RibbonControlGroupProps } from "../RibbonControlGroup";
import type {
  CachedRibbonScalingState,
  RibbonScalingData,
  RibbonScalingState,
  RibbonScalingStateMLR,
  RibbonScalingStateSLR,
  ScaledRibbonGroup,
  ScaledRibbonGroupMLR,
  ScaledRibbonGroupSLR,
} from "./ribbonBottomBarScalingTypes";
import { getDefaultLayout, scopedLayout, singleRow } from "..";
import { replaceElementInControlArray } from "./ribbonBottomBarScalingUtils";

type DroppedLabelsDict = { [controlId: string]: boolean };

function getIndexOfControlIdInGroup(
  controlGroup: RibbonControlGroupDefinition,
  controlId: string
): number {
  return findIndex(
    controlGroup.controls,
    (item: RibbonControlProps) => item.id === controlId
  );
}

/**
 * Gets a subset of scaling steps that should be applied
 * @param scalingSteps The full list of scaling steps
 * @param numberOfScalingStepsToApply The number of scaling steps that should be applied
 */
const getScalingTransformationsToApply = memoizeFunction(
  (
    scalingSteps: Readonly<RibbonScalingStep>[],
    numberOfScalingStepsToApply: number
  ): RibbonScalingTransformation[] => {
    const scalingStepsToApply = scalingSteps.slice(
      0,
      numberOfScalingStepsToApply
    );

    // Flatten the list of scaling steps to apply into a list of scaling transformations
    let result = [] as RibbonScalingTransformation[];
    scalingStepsToApply.forEach((scalingStep) => {
      result = result.concat(scalingStep);
    });

    return result;
  }
);

function dropLabelFromControlProps(
  controlProps: RibbonControlProps
): RibbonControlProps {
  // Since AppComboBoxProps uses a different visibility command, refactoring of
  // the combo box is tracked by VSO#3234273
  if (controlProps.type === "AppComboBoxProps") {
    return { ...controlProps, showLabel: false };
  }

  if (
    controlProps.type === "AppButtonProps" ||
    controlProps.type === "AppInkButtonProps" ||
    controlProps.type === "AppToggleButtonProps" ||
    controlProps.type === "AppFlyoutAnchorProps" ||
    controlProps.type === "AppInkFlyoutAnchorProps" ||
    controlProps.type === "AppSplitButtonProps"
  ) {
    return { ...controlProps, hideLabel: true };
  }

  if (controlProps.type === "AppFontPickerProps") {
    return controlProps;
  }

  return transformIfPreviewGalleryProps(
    controlProps,
    (galleryProps: RibbonPreviewGalleryPropsUnion) => {
      if (galleryProps.renderAsFlyout) {
        return {
          ...galleryProps,
          flyoutAnchorProps: {
            ...galleryProps.flyoutAnchorProps,
            hideLabel: true,
          },
        };
      }

      return galleryProps;
    }
  );
}

/**
 * If the given Ribbon control props are a preview gallery, apply the given transformation to another RibbonControlProps.
 * Otherwise, return the original props.
 */
function transformIfPreviewGalleryProps(
  controlProps: RibbonControlProps,
  transformFn: (
    controlProps: RibbonPreviewGalleryPropsUnion
  ) => RibbonControlProps
): RibbonControlProps {
  if (
    controlProps.type === "AppFontStylesPreviewGalleryProps" ||
    controlProps.type === "AppIconPreviewGalleryProps" ||
    controlProps.type === "RibbonTableStylesPreviewGalleryProps" ||
    controlProps.type === "RibbonChangeColorsPreviewGalleryProps"
  ) {
    return transformFn(controlProps);
  }
  return controlProps;
}

export function convertPreviewGalleryToFlyoutAnchor(
  controlProps: RibbonControlProps
): RibbonControlProps {
  return transformIfPreviewGalleryProps(
    controlProps,
    (galleryProps: RibbonPreviewGalleryPropsUnion) => ({
      ...galleryProps,
      renderAsFlyout: true,
    })
  );
}

function dropPreviewItemFromGallery(
  controlProps: RibbonControlProps
): RibbonControlProps {
  return transformIfPreviewGalleryProps(
    controlProps,
    (galleryProps: RibbonPreviewGalleryPropsUnion) => {
      if (galleryProps.galleryButtons.length > 1) {
        // Typecast is necessary here because TypeScript doesn't handle generics well in the spread operator.
        return {
          ...controlProps,
          galleryButtons: galleryProps.galleryButtons.slice(0, -1),
        } as RibbonControlProps;
      }
      return controlProps;
    }
  );
}

// If we have two pinned combo boxes in control list and we are going to move one of them to overflow
// we should unpin the second one so it will be rendered correctly.
function unpinNextComboboxIfNeeded(
  controls: RibbonControlProps[],
  controlToScaleIndex: number
) {
  // if this is not last control in top level controls
  if (controlToScaleIndex + 1 < controls.length) {
    const nextTopLevelControl = controls[controlToScaleIndex + 1];
    // and both this and next are combobox controls
    if (
      controls[controlToScaleIndex].type === "AppComboBoxProps" &&
      nextTopLevelControl.type === "AppComboBoxProps"
    ) {
      // lets set pinToPreviousComboBox property on second combo box to false
      return replaceElement(
        controls,
        { ...nextTopLevelControl, pinToPreviousComboBox: false },
        controlToScaleIndex + 1
      );
    }
  }
  return controls;
}

function applyScalingTransformationsToControlGroupInternal(
  controlGroup: RibbonControlGroupDefinitionSLR,
  scalingTransformations: RibbonScalingTransformation[],
  controlRendererFunction?: ControlRendererFunction,
  controlInMenuRendererFunction?: ControlInMenuRendererFunction,
  initialScaledRibbonGroup?: ScaledRibbonGroupSLR,
  getScaledRibbonGroupsForResponsiveRendering?: boolean,
  overscalingSafeguard?: boolean
): ScaledRibbonGroupSLR;
function applyScalingTransformationsToControlGroupInternal(
  controlGroup: RibbonControlGroupDefinitionMLR,
  scalingTransformations: RibbonScalingTransformation[],
  controlRendererFunction?: ControlRendererFunction,
  controlInMenuRendererFunction?: ControlInMenuRendererFunction,
  initialScaledRibbonGroup?: ScaledRibbonGroupMLR,
  getScaledRibbonGroupsForResponsiveRendering?: boolean,
  overscalingSafeguard?: boolean
): ScaledRibbonGroupMLR;
function applyScalingTransformationsToControlGroupInternal(
  controlGroup: RibbonControlGroupDefinition,
  scalingTransformations: RibbonScalingTransformation[],
  controlRendererFunction?: ControlRendererFunction,
  controlInMenuRendererFunction?: ControlInMenuRendererFunction,
  initialScaledRibbonGroup?: ScaledRibbonGroup,
  getScaledRibbonGroupsForResponsiveRendering?: boolean,
  overscalingSafeguard?: boolean
): ScaledRibbonGroup {
  switch (controlGroup.type) {
    case "SingleLine":
      return applyScalingTransformationsToControlGroupSLR(
        controlGroup,
        scalingTransformations,
        getScaledRibbonGroupsForResponsiveRendering,
        overscalingSafeguard
      );
    case "Multiline":
      return applyScalingTransformationsToControlGroupMLR(
        controlGroup,
        scalingTransformations,
        controlRendererFunction,
        controlInMenuRendererFunction,
        initialScaledRibbonGroup as ScaledRibbonGroupMLR,
        getScaledRibbonGroupsForResponsiveRendering
      );
  }
}
/**
 * Applies a given set of scaling transformations to a single Ribbon control group
 */
export const applyScalingTransformationsToControlGroup = memoizeFunction(
  applyScalingTransformationsToControlGroupInternal
);

/**
 * Applies a given set of scaling transformations to a single SLR control group
 */
const applyScalingTransformationsToControlGroupSLR = (
  controlGroup: RibbonControlGroupDefinitionSLR,
  scalingTransformations: RibbonScalingTransformation[],
  getScaledRibbonGroupsForResponsiveRendering?: boolean,
  overscalingSafeguard?: boolean
): ScaledRibbonGroupSLR => {
  return getScaledRibbonGroupsForResponsiveRendering
    ? applyResponsiveScalingTransformationsToControlGroupSLR(
        controlGroup,
        scalingTransformations,
        overscalingSafeguard
      )
    : applyFullScalingTransformationsToControlGroupSLR(
        controlGroup,
        scalingTransformations
      );
};

/**
 * Applies a given set of scaling transformations to a single MLR control group
 */
const applyScalingTransformationsToControlGroupMLR = (
  controlGroup: RibbonControlGroupDefinitionMLR,
  scalingTransformations: RibbonScalingTransformation[],
  controlRendererFunction?: ControlRendererFunction,
  controlInMenuRendererFunction?: ControlInMenuRendererFunction,
  initialScaledRibbonGroup?: ScaledRibbonGroupMLR,
  getScaledRibbonGroupsForResponsiveRendering?: boolean
): ScaledRibbonGroupMLR => {
  return getScaledRibbonGroupsForResponsiveRendering
    ? applyResponsiveScalingTransformationsToControlGroupMLR(
        controlGroup,
        scalingTransformations,
        controlRendererFunction,
        controlInMenuRendererFunction,
        initialScaledRibbonGroup
      )
    : applyFullScalingTransformationsToControlGroupMLR(
        controlGroup,
        scalingTransformations,
        controlRendererFunction,
        controlInMenuRendererFunction,
        initialScaledRibbonGroup
      );
};

/**
 * Applies a given set of scaling transformations to a single SLR control group
 */
const applyFullScalingTransformationsToControlGroupSLR = memoizeFunction(
  (
    controlGroup: RibbonControlGroupDefinitionSLR,
    scalingTransformations: RibbonScalingTransformation[]
  ): ScaledRibbonGroupSLR => {
    let topLevelControls = controlGroup.controls;
    let overflowControls: RibbonControlProps[] = [];

    let hasOwnOverflow = controlGroup.hasOwnOverflow;
    let shouldAllowAllControlsInOverflow =
      controlGroup.shouldAllowAllControlsInOverflow;

    scalingTransformations.forEach((scalingTransformation) => {
      switch (scalingTransformation.type) {
        case "MoveToOverflow": {
          ({ topLevelControls, overflowControls } =
            moveToOverflowScalingTransformation(
              topLevelControls,
              scalingTransformation,
              controlGroup,
              overflowControls
            ));
          break;
        }
        case "GalleryToFlyout":
          topLevelControls = replaceElementInControlArray<RibbonControlProps>(
            topLevelControls,
            scalingTransformation.controlId,
            convertPreviewGalleryToFlyoutAnchor
          );
          break;
        case "DropLabel":
          topLevelControls = replaceElementInControlArray<RibbonControlProps>(
            topLevelControls,
            scalingTransformation.controlId,
            dropLabelFromControlProps
          );
          break;
        case "DropPreviewItemFromGallery": {
          topLevelControls = replaceElementInControlArray<RibbonControlProps>(
            topLevelControls,
            scalingTransformation.controlId,
            dropPreviewItemFromGallery
          );
          break;
        }
        case "MoveGroupToSingleOverflow":
          if (controlGroup.id === scalingTransformation.groupId) {
            hasOwnOverflow = false;
            shouldAllowAllControlsInOverflow = true;
          }
          break;
        case "ChangeGroupLayout":
          throw new Error(
            "ChangeGroupLayout should not be set on the Single Line Ribbon"
          );
        case "GroupToFlyout":
          throw new Error(
            "GroupToFlyout should not be set on the Single Line Ribbon"
          );
        default:
          assertNever(scalingTransformation);
      }
    });

    // Sort controls in the overflow well based on their original order
    overflowControls = overflowControls.slice().sort((a, b) => {
      return (
        getIndexOfControlIdInGroup(controlGroup, a.id) -
        getIndexOfControlIdInGroup(controlGroup, b.id)
      );
    });

    return {
      type: "SingleLine",
      id: controlGroup.id,
      overflowMenuLauncherDisabled: controlGroup.overflowMenuLauncherDisabled,
      overflowMenuLauncherLabel: controlGroup.overflowMenuLauncherLabel,
      onOverflowMenuOpen: controlGroup.onOverflowMenuOpen,
      onOverflowMenuClose: controlGroup.onOverflowMenuClose,
      hasOwnOverflow,
      shouldAllowAllControlsInOverflow,
      topLevelControls,
      overflowControls,
      keytipProps: controlGroup.keytipProps,
      groupLabel: controlGroup.title,
      fastDisabled: controlGroup.fastDisabled,
    };
  }
);

/**
 * Applies a given set of scaling transformations to a single SLR control group
 */
const applyResponsiveScalingTransformationsToControlGroupSLR = memoizeFunction(
  (
    controlGroup: RibbonControlGroupDefinitionSLR,
    scalingTransformations: RibbonScalingTransformation[],
    overscalingSafeguard?: boolean
  ): ScaledRibbonGroupSLR => {
    let topLevelControls = controlGroup.controls;
    let overflowControls: RibbonControlProps[] = [];

    let hasOwnOverflow = controlGroup.hasOwnOverflow;
    let shouldAllowAllControlsInOverflow =
      controlGroup.shouldAllowAllControlsInOverflow;

    // Keep track of if the group is, switches to using the single overflow,
    // or does not have a behavior defined
    let movedToSingleOverflow =
      overscalingSafeguard &&
      ((!hasOwnOverflow && shouldAllowAllControlsInOverflow) ||
        (shouldAllowAllControlsInOverflow === undefined &&
          hasOwnOverflow === undefined));

    scalingTransformations.forEach((scalingTransformation) => {
      switch (scalingTransformation.type) {
        case "MoveToOverflow": {
          // If there is only one control that is being scaled,
          // allow the control in the overflow so that it does not get
          // completely hidden by the responsive CSS
          if (topLevelControls.length === 1) {
            movedToSingleOverflow = overscalingSafeguard;
          }
          ({ topLevelControls, overflowControls } =
            moveToOverflowScalingTransformation(
              topLevelControls,
              scalingTransformation,
              controlGroup,
              overflowControls,
              movedToSingleOverflow
            ));
          break;
        }
        case "MoveGroupToSingleOverflow":
          if (controlGroup.id === scalingTransformation.groupId) {
            hasOwnOverflow = false;
            shouldAllowAllControlsInOverflow = true;
            movedToSingleOverflow = overscalingSafeguard;
          }
          break;
      }
    });

    // Sort controls in the overflow well based on their original order
    overflowControls = overflowControls.slice().sort((a, b) => {
      return (
        getIndexOfControlIdInGroup(controlGroup, a.id) -
        getIndexOfControlIdInGroup(controlGroup, b.id)
      );
    });

    return {
      type: "SingleLine",
      id: controlGroup.id,
      overflowMenuLauncherDisabled: controlGroup.overflowMenuLauncherDisabled,
      overflowMenuLauncherLabel: controlGroup.overflowMenuLauncherLabel,
      onOverflowMenuOpen: controlGroup.onOverflowMenuOpen,
      onOverflowMenuClose: controlGroup.onOverflowMenuClose,
      hasOwnOverflow,
      shouldAllowAllControlsInOverflow,
      topLevelControls,
      overflowControls,
      keytipProps: controlGroup.keytipProps,
      groupLabel: controlGroup.title,
      fastDisabled: controlGroup.fastDisabled,
    };
  }
);

/**
 * Applies a given set of scaling transformations to a single MLR control group
 */
const applyFullScalingTransformationsToControlGroupMLR = memoizeFunction(
  (
    controlGroup: RibbonControlGroupDefinitionMLR,
    scalingTransformations: RibbonScalingTransformation[],
    controlRendererFunction?: ControlRendererFunction,
    controlInMenuRendererFunction?: ControlInMenuRendererFunction,
    initialScaledRibbonGroup?: ScaledRibbonGroupMLR
  ): ScaledRibbonGroupMLR => {
    let controls = controlGroup.controls;
    let layout = (controlGroup.layouts && controlGroup.layouts[0]) || undefined;
    let layoutIndex = 0;
    let hideGroupLabel = controlGroup.hideGroupLabel;
    scalingTransformations.forEach((scalingTransformation) => {
      switch (scalingTransformation.type) {
        case "DropLabel":
          controls = replaceElementInControlArray<RibbonControlProps>(
            controls,
            scalingTransformation.controlId,
            dropLabelFromControlProps
          );
          break;
        case "GalleryToFlyout":
          controls = replaceElementInControlArray<RibbonControlProps>(
            controls,
            scalingTransformation.controlId,
            convertPreviewGalleryToFlyoutAnchor
          );
          break;
        case "DropPreviewItemFromGallery": {
          controls = replaceElementInControlArray<RibbonControlProps>(
            controls,
            scalingTransformation.controlId,
            dropPreviewItemFromGallery
          );
          break;
        }
        case "ChangeGroupLayout":
          if (
            controlGroup.id === scalingTransformation.groupId &&
            // If there's another available layout, switch to it
            controlGroup.layouts &&
            controlGroup.layouts.length - 1 > layoutIndex
          ) {
            layoutIndex += 1;
            layout = controlGroup.layouts[layoutIndex];
          }
          break;
        case "GroupToFlyout":
          if (
            controlGroup.id === scalingTransformation.groupId &&
            controlGroup.controls.length > 1 &&
            controlGroup.commonGroupFlyout &&
            controlRendererFunction &&
            controlInMenuRendererFunction &&
            initialScaledRibbonGroup
          ) {
            controls = convertGroupToFlyout(
              controlGroup,
              initialScaledRibbonGroup.controls,
              controlRendererFunction!,
              controlInMenuRendererFunction!
            );
            layout = { sections: [[[controlGroup.commonGroupFlyout!.id]]] };
            hideGroupLabel = true;
          }
          break;
      }
    });

    return {
      type: "Multiline",
      id: controlGroup.id,
      controls,
      groupLabel: controlGroup.title,
      layout,
      groupLauncher: controlGroup.groupLauncher,
      hideGroupLabel,
      disableAnimations: controlGroup.disableAnimations,
      fastDisabled: controlGroup.fastDisabled,
      forceHideGroupLabelOnly: controlGroup.forceHideGroupLabelOnly,
    };
  }
);

/**
 * Applies a given set of scaling transformations for responsive scaling to a single MLR control group
 */
const applyResponsiveScalingTransformationsToControlGroupMLR = memoizeFunction(
  (
    controlGroup: RibbonControlGroupDefinitionMLR,
    scalingTransformations: RibbonScalingTransformation[],
    controlRendererFunction?: ControlRendererFunction,
    controlInMenuRendererFunction?: ControlInMenuRendererFunction,
    initialScaledRibbonGroup?: ScaledRibbonGroupMLR
  ): ScaledRibbonGroupMLR => {
    let controls = controlGroup.controls;
    const layout =
      (controlGroup.layouts && controlGroup.layouts[0]) || undefined;
    let hideGroupLabel = controlGroup.hideGroupLabel;
    let responsiveLayout = controlGroup.responsiveLayout || undefined;
    const droppedLabels: DroppedLabelsDict = {};
    scalingTransformations.forEach((scalingTransformation) => {
      // Take note of dropped labels so that groupToFlyout will render correctly
      if (scalingTransformation.type === "DropLabel") {
        droppedLabels[scalingTransformation.controlId] = true;
      }

      // We only need to process GroupToFlyout scaling transformations
      // that also have all valid pieces for creating FlyoutAnchorProps
      if (
        scalingTransformation.type !== "GroupToFlyout" ||
        controlGroup.id !==
          (scalingTransformation as ConvertGroupToFlyout).groupId ||
        controlGroup.controls.length < 1 ||
        !controlGroup.commonGroupFlyout ||
        !controlRendererFunction ||
        !controlInMenuRendererFunction ||
        !initialScaledRibbonGroup
      ) {
        return;
      }

      controls = convertGroupToFlyout(
        controlGroup,
        initialScaledRibbonGroup.controls,
        controlRendererFunction!,
        controlInMenuRendererFunction!,
        droppedLabels
      );

      const groupToFlyoutSection = scopedLayout(
        -1 /* GroupToFlyoutIndex */,
        singleRow(controlGroup.commonGroupFlyout!.id)
      );

      // If a responsive layout is defined, we need to add the group flyout to the definition,
      // otherwise attempt to use the base layout (for less complex group layouts)
      if (responsiveLayout) {
        const lastSection = responsiveLayout.sections[
          responsiveLayout.sections.length - 1
        ] as any;
        if (
          typeof lastSection !== "object" ||
          Array.isArray(lastSection) ||
          lastSection.layoutIndex !== -1
        ) {
          responsiveLayout.sections.push(groupToFlyoutSection);
        }
      } else if (layout) {
        responsiveLayout = {
          sections: [...layout.sections, groupToFlyoutSection],
        };
      } else {
        responsiveLayout = getDefaultLayout(controlGroup.controls);
        responsiveLayout.sections.push(groupToFlyoutSection);
      }

      hideGroupLabel = true;
    });

    return {
      type: "Multiline",
      id: controlGroup.id,
      controls,
      groupLabel: controlGroup.title,
      layout,
      responsiveLayout,
      groupLauncher: controlGroup.groupLauncher,
      hideGroupLabel,
      disableAnimations: controlGroup.disableAnimations,
      fastDisabled: controlGroup.fastDisabled,
      forceHideGroupLabelOnly: controlGroup.forceHideGroupLabelOnly,
    };
  }
);

/**
 * Whenever a ribbon control group requests its overflow menu to be moved and merged with the next
 *  control group, we try to satisfy this, by rearranging the overflow menus accordingly
 * @param scaledRibbonGroups The scaled Ribbon groups that need to be transformed
 * @returns Scaled groups after the overflow menu regrouping
 */
const moveOverflowMenusToRibbonEnd = memoizeFunction(
  (
    scaledRibbonGroups: ScaledRibbonGroupSLR[]
  ): [ScaledRibbonGroupSLR[], MenuDefinition] => {
    const combinedOverflowMenu: MenuDefinition = {
      sections: [],
    };
    scaledRibbonGroups.forEach((group) => {
      if (!group.hasOwnOverflow && group.overflowControls.length !== 0) {
        // carry controls over
        combinedOverflowMenu.sections.push({
          title: group.groupLabel,
          controls: [...group.overflowControls],
          id: group.id,
        });
      }
    });
    return [scaledRibbonGroups, combinedOverflowMenu];
  }
);

/**
 * Helper function for applying the MoveToOverflow ScalingTransformation
 * @param topLevelControls An array of top level controls
 * @param scalingTransformation The MoveToOverflow scaling transformation to apply
 * @param controlGroup The control group definition
 * @param overflowControls An array of overflow controls
 * @returns
 */
function moveToOverflowScalingTransformation(
  topLevelControls: RibbonControlProps[],
  scalingTransformation: MoveToOverflow,
  controlGroup: RibbonControlGroupDefinitionSLR,
  overflowControls: RibbonControlProps[],
  isMovedToSingleOverflow?: boolean
) {
  let updatedTopLevelControls = topLevelControls;
  let updatedOverflowControls = overflowControls;
  const controlIndexInTopLevel = findIndex(
    updatedTopLevelControls,
    (control: RibbonControlProps) =>
      control.id === scalingTransformation.controlId
  );
  const movedToSingleOverflow =
    !!isMovedToSingleOverflow ||
    (!!controlGroup.shouldAllowAllControlsInOverflow &&
      controlGroup.hasOwnOverflow === false);
  if (
    controlIndexInTopLevel !== -1 &&
    (updatedTopLevelControls.length > 1 ||
      movedToSingleOverflow ||
      // If a control group only has a single control that it is displaying, allow it to move
      // into the overflow at the end of the bottom bar.
      controlGroup.controls.length === 1)
  ) {
    updatedTopLevelControls = unpinNextComboboxIfNeeded(
      updatedTopLevelControls,
      controlIndexInTopLevel
    );
    updatedOverflowControls = [
      ...updatedOverflowControls,
      { ...updatedTopLevelControls[controlIndexInTopLevel] },
    ];
    updatedTopLevelControls = updatedTopLevelControls.filter(
      (control) => control.id !== scalingTransformation.controlId
    );
  }
  return {
    topLevelControls: updatedTopLevelControls,
    overflowControls: updatedOverflowControls,
  };
}

function getScaledRibbonGroupsInternal(
  ribbonGroupDefinitions: RibbonControlGroupDefinitionSetSLR,
  scalingSteps: Readonly<RibbonScalingStep>[],
  numberOfScalingStepsToApply: number,
  controlRendererFunction?: ControlRendererFunction,
  controlInMenuRendererFunction?: ControlInMenuRendererFunction,
  initialScaledRibbonGroups?: ScaledRibbonGroupSLR[],
  getScaledRibbonGroupsForResponsiveRendering?: boolean,
  overscalingSafeguard?: boolean
): [ScaledRibbonGroupSLR[], MenuDefinition];
function getScaledRibbonGroupsInternal(
  ribbonGroupDefinitions: RibbonControlGroupDefinitionSetMLR,
  scalingSteps: Readonly<RibbonScalingStep>[],
  numberOfScalingStepsToApply: number,
  controlRendererFunction?: ControlRendererFunction,
  controlInMenuRendererFunction?: ControlInMenuRendererFunction,
  initialScaledRibbonGroups?: ScaledRibbonGroupMLR[],
  getScaledRibbonGroupsForResponsiveRendering?: boolean,
  overscalingSafeguard?: boolean
): [ScaledRibbonGroupMLR[]];
function getScaledRibbonGroupsInternal(
  ribbonGroupDefinitions: RibbonControlGroupDefinitionSet,
  scalingSteps: Readonly<RibbonScalingStep>[],
  numberOfScalingStepsToApply: number,
  controlRendererFunction?: ControlRendererFunction,
  controlInMenuRendererFunction?: ControlInMenuRendererFunction,
  initialScaledRibbonGroups?: ScaledRibbonGroup[],
  getScaledRibbonGroupsForResponsiveRendering?: boolean,
  overscalingSafeguard?: boolean
): [ScaledRibbonGroupSLR[], MenuDefinition] | [ScaledRibbonGroupMLR[]] {
  const scalingStepsToApply = getScalingTransformationsToApply(
    scalingSteps,
    numberOfScalingStepsToApply
  );

  switch (ribbonGroupDefinitions.type) {
    case "SingleLine": {
      const scaledRibbonGroups = ribbonGroupDefinitions.groups.map(
        (controlGroup) =>
          applyScalingTransformationsToControlGroup(
            controlGroup,
            scalingStepsToApply,
            controlRendererFunction,
            controlInMenuRendererFunction,
            undefined,
            getScaledRibbonGroupsForResponsiveRendering,
            overscalingSafeguard
          )
      );
      const scaledRibbonGroupsSLR =
        moveOverflowMenusToRibbonEnd(scaledRibbonGroups);
      const filteredScaledRibbonGroupsSLR =
        removeEmptyGroupsFromScaledRibbonGroupsSLR(
          scaledRibbonGroupsSLR[0],
          getScaledRibbonGroupsForResponsiveRendering,
          overscalingSafeguard
        );
      return [filteredScaledRibbonGroupsSLR, scaledRibbonGroupsSLR[1]];
    }
    case "Multiline": {
      const scaledRibbonGroups = ribbonGroupDefinitions.groups.map(
        (controlGroup, index) =>
          applyScalingTransformationsToControlGroup(
            controlGroup,
            scalingStepsToApply,
            controlRendererFunction,
            controlInMenuRendererFunction,
            initialScaledRibbonGroups
              ? (initialScaledRibbonGroups[index] as ScaledRibbonGroupMLR)
              : undefined,
            getScaledRibbonGroupsForResponsiveRendering,
            overscalingSafeguard
          )
      );
      return [scaledRibbonGroups];
    }
  }
}

/**
 * Remove Empty Groups from the list of scaled ribbon groups
 */
function removeEmptyGroupsFromScaledRibbonGroupsSLR(
  scaledRibbonGroups: ScaledRibbonGroupSLR[],
  isResponsiveRendering?: boolean,
  overscalingSafeguard?: boolean
): ScaledRibbonGroupSLR[] {
  const nonEmptyScaledRibbonControlGroups = scaledRibbonGroups.filter(
    (controlGroup) =>
      (isResponsiveRendering && overscalingSafeguard
        ? controlGroup.topLevelControls.length +
          controlGroup.overflowControls.length
        : controlGroup.topLevelControls.length) > 0
  );
  return nonEmptyScaledRibbonControlGroups;
}

/**
 * Gets the scaled Ribbon groups
 * @param ribbonGroupDefinitions The Ribbon groups that will be transformed
 * @param scalingSteps The set of scaling steps that can be applied
 * @param numberOfScalingStepsToApply The number of scaling steps that should be applied
 */
const getScaledRibbonGroups = memoizeFunction(getScaledRibbonGroupsInternal);

/**
 * Gets the next scaling step after applying some scaling steps
 * @param scalingState The last known scaling step
 * @param numberOfScalingStepsToApply The number of scaling steps to apply on the control groups
 */
function getNextScalingState(
  scalingState: RibbonScalingStateSLR,
  numberOfScalingStepsToApply: number
): RibbonScalingStateSLR;
function getNextScalingState(
  scalingState: RibbonScalingStateMLR,
  numberOfScalingStepsToApply: number
): RibbonScalingStateMLR;
function getNextScalingState(
  scalingState: RibbonScalingState,
  numberOfScalingStepsToApply: number
): RibbonScalingState {
  switch (scalingState.type) {
    case "SingleLine":
      const [slrRibbonGroups, scaledEndOfRibbonOverflowMenu] =
        getScaledRibbonGroups(
          scalingState.ribbonGroupDefinitions,
          scalingState.scalingSteps,
          numberOfScalingStepsToApply
        );

      return {
        ...scalingState,
        scaledRibbonGroups: slrRibbonGroups,
        scaledEndOfRibbonOverflowMenu,
        appliedScalingStepCount: numberOfScalingStepsToApply,
      };
    case "Multiline":
      const [mlrRibbonGroups] = getScaledRibbonGroups(
        scalingState.ribbonGroupDefinitions,
        scalingState.scalingSteps,
        numberOfScalingStepsToApply,
        scalingState.controlRendererFunction,
        scalingState.controlInMenuRendererFunction,
        scalingState.initialScaledRibbonGroups
      );

      return {
        ...scalingState,
        scaledRibbonGroups: mlrRibbonGroups,
        appliedScalingStepCount: numberOfScalingStepsToApply,
      };
  }
}

/**
 * Applies the next scaling operation and returns the next Ribbon scaling state.
 * @param scalingState The current scaling state of the Ribbon.
 */
export function scaleRibbonDown(
  scalingState: RibbonScalingStateSLR,
  scalingStepsToApply?: number
): RibbonScalingStateSLR | undefined;
export function scaleRibbonDown(
  scalingState: RibbonScalingStateMLR,
  scalingStepsToApply?: number
): RibbonScalingStateMLR | undefined;
export function scaleRibbonDown(
  scalingState: RibbonScalingState,
  scalingStepsToApply?: number
): RibbonScalingState | undefined {
  if (
    scalingState.appliedScalingStepCount >= scalingState.scalingSteps.length
  ) {
    return undefined;
  }

  const numberOfScalingStepsToApply =
    scalingState.appliedScalingStepCount + (scalingStepsToApply ?? 1);
  switch (scalingState.type) {
    case "SingleLine":
      return getNextScalingState(scalingState, numberOfScalingStepsToApply);
    case "Multiline":
      return getNextScalingState(scalingState, numberOfScalingStepsToApply);
  }
}

/**
 * Undoes the last scaling step applied to the control groups
 * @param scalingState The current scaling state of the Ribbon
 */
export function scaleRibbonUp(
  scalingState: RibbonScalingStateSLR
): RibbonScalingStateSLR | undefined;
export function scaleRibbonUp(
  scalingState: RibbonScalingStateMLR
): RibbonScalingStateMLR | undefined;
export function scaleRibbonUp(
  scalingState: RibbonScalingState
): RibbonScalingState | undefined {
  if (
    scalingState.appliedScalingStepCount <=
    scalingState.minimumScalingStepsToApply
  ) {
    return undefined;
  }

  const numberOfScalingStepsToApply = scalingState.appliedScalingStepCount - 1;
  switch (scalingState.type) {
    case "SingleLine":
      return getNextScalingState(scalingState, numberOfScalingStepsToApply);
    case "Multiline":
      return getNextScalingState(scalingState, numberOfScalingStepsToApply);
  }
}

/**
 * Applies the number of scaling steps specified in the second parameter.
 * This function needs to make a best case effort to apply the given number of scaling steps.
 * There will always be a check in the AppResizeGroup to make sure that the scaled ribbon
 * returned from this function fits so we should never render the wrong layout even if our
 * best case effort turns out to be wrong.
 */
export function batchScaleRibbon(
  scalingState: RibbonScalingStateSLR,
  scalingStepsToApply: number
): RibbonScalingStateSLR | undefined;
export function batchScaleRibbon(
  scalingState: RibbonScalingStateMLR,
  scalingStepsToApply: number
): RibbonScalingStateMLR | undefined;
export function batchScaleRibbon(
  scalingState: RibbonScalingState,
  scalingStepsToApply: number
): RibbonScalingState | undefined {
  let nextScalingSteps = scalingStepsToApply;
  if (scalingStepsToApply < scalingState.minimumScalingStepsToApply) {
    nextScalingSteps = scalingState.minimumScalingStepsToApply;
  } else if (scalingStepsToApply >= scalingState.scalingSteps.length) {
    nextScalingSteps = scalingState.scalingSteps.length - 1;
  }

  switch (scalingState.type) {
    case "SingleLine":
      return getNextScalingState(scalingState, nextScalingSteps);
    case "Multiline":
      return getNextScalingState(scalingState, nextScalingSteps);
  }
}

/**
 * Merges two scaling step arrays.
 */
const mergeScalingSteps = memoizeFunction(
  (
    scalingSteps: Readonly<RibbonScalingStep>[],
    initialScalingSteps: Readonly<RibbonScalingStep>[]
  ): Readonly<RibbonScalingStep>[] => {
    return [...initialScalingSteps, ...scalingSteps];
  }
);

/**
 * Generates the initial scaling state for the Ribbon.
 * @param ribbonGroupDefinitions The definition of the various control groups.
 * @param scalingSteps The scaling steps that can be applied to the Ribbon.
 * @param initialScalingSteps A set of scaling steps that should always be applied to the Ribbon groups, regardless of how much room is available
 */
export function getInitialScalingState(
  id: string,
  ribbonGroupDefinitions: RibbonControlGroupDefinitionSetSLR,
  scalingSteps: Readonly<RibbonScalingStep>[],
  initialScalingSteps?: Readonly<RibbonScalingStep>[],
  controlRendererFunction?: ControlRendererFunction,
  controlInMenuRendererFunction?: ControlInMenuRendererFunction,
  initialStepsToApply?: number,
  getScaledRibbonGroupsForResponsiveRendering?: boolean,
  overscalingSafeguard?: boolean
): RibbonScalingStateSLR;
export function getInitialScalingState(
  id: string,
  ribbonGroupDefinitions: RibbonControlGroupDefinitionSetMLR,
  scalingSteps: Readonly<RibbonScalingStep>[],
  initialScalingSteps?: Readonly<RibbonScalingStep>[],
  controlRendererFunction?: ControlRendererFunction,
  controlInMenuRendererFunction?: ControlInMenuRendererFunction,
  initialStepsToApply?: number,
  getScaledRibbonGroupsForResponsiveRendering?: boolean,
  overscalingSafeguard?: boolean
): RibbonScalingStateMLR;
export function getInitialScalingState(
  id: string,
  ribbonGroupDefinitions: RibbonControlGroupDefinitionSet,
  scalingSteps: Readonly<RibbonScalingStep>[],
  initialScalingSteps?: Readonly<RibbonScalingStep>[],
  controlRendererFunction?: ControlRendererFunction,
  controlInMenuRendererFunction?: ControlInMenuRendererFunction,
  initialStepsToApply?: number,
  getScaledRibbonGroupsForResponsiveRendering?: boolean,
  overscalingSafeguard?: boolean
): RibbonScalingState;
export function getInitialScalingState(
  id: string,
  ribbonGroupDefinitions: RibbonControlGroupDefinitionSet,
  scalingSteps: Readonly<RibbonScalingStep>[],
  initialScalingSteps: Readonly<RibbonScalingStep>[] = [],
  controlRendererFunction?: ControlRendererFunction,
  controlInMenuRendererFunction?: ControlInMenuRendererFunction,
  initialStepsToApply?: number,
  getScaledRibbonGroupsForResponsiveRendering?: boolean,
  overscalingSafeguard?: boolean
): RibbonScalingState {
  const minimumScalingStepsToApply =
    initialStepsToApply ?? initialScalingSteps.length;
  const appliedScalingStepCount = minimumScalingStepsToApply;
  const combinedScalingSteps = mergeScalingSteps(
    scalingSteps,
    initialScalingSteps
  );

  switch (ribbonGroupDefinitions.type) {
    case "SingleLine":
      let [scaledRibbonGroups, scaledEndOfRibbonOverflowMenu] =
        getScaledRibbonGroups(
          ribbonGroupDefinitions,
          combinedScalingSteps,
          appliedScalingStepCount,
          controlRendererFunction,
          controlInMenuRendererFunction,
          undefined /* initialScaledRibbonGroups */,
          getScaledRibbonGroupsForResponsiveRendering,
          overscalingSafeguard
        );

      // If we are responsive rendering we need both the complete overflow controls
      // and end of ribbon overflow menu definition so that we can represent all scaling states
      if (getScaledRibbonGroupsForResponsiveRendering) {
        const [
          responsiveScaledRibbonGroups,
          responsiveScaledEndOfRibbonOverflowMenu,
        ] = getScaledRibbonGroups(
          ribbonGroupDefinitions,
          combinedScalingSteps,
          combinedScalingSteps.length,
          controlRendererFunction,
          controlInMenuRendererFunction,
          undefined /* initialScaledRibbonGroups */,
          getScaledRibbonGroupsForResponsiveRendering,
          overscalingSafeguard
        );
        scaledRibbonGroups = scaledRibbonGroups.map((group) => {
          const fullyScaledGroup = responsiveScaledRibbonGroups.find(
            (responsiveGroup) => group.id === responsiveGroup.id
          );

          if (fullyScaledGroup) {
            group.overflowControls = fullyScaledGroup.overflowControls;
          }

          return group;
        });

        scaledEndOfRibbonOverflowMenu = responsiveScaledEndOfRibbonOverflowMenu;
      }

      return {
        id,
        type: "SingleLine",
        appliedScalingStepCount,
        minimumScalingStepsToApply,
        scaledEndOfRibbonOverflowMenu,
        ribbonGroupDefinitions,
        scaledRibbonGroups,
        scalingSteps: combinedScalingSteps,
      };

    case "Multiline":
      let [scaledRibbonGroupsMLR] = getScaledRibbonGroups(
        ribbonGroupDefinitions,
        combinedScalingSteps,
        appliedScalingStepCount,
        controlRendererFunction,
        controlInMenuRendererFunction,
        undefined /* initialScaledRibbonGroups */,
        getScaledRibbonGroupsForResponsiveRendering,
        overscalingSafeguard
      );
      // If we are responsive rendering we need to also get the
      // final scaling state so that we can add any renderGroupAsGallery items
      if (getScaledRibbonGroupsForResponsiveRendering) {
        const [responsiveScaledRibbonGroupsMLR] = getScaledRibbonGroups(
          ribbonGroupDefinitions,
          combinedScalingSteps,
          combinedScalingSteps.length,
          controlRendererFunction,
          controlInMenuRendererFunction,
          // undefined /* initialScaledRibbonGroups */,
          scaledRibbonGroupsMLR,
          getScaledRibbonGroupsForResponsiveRendering,
          overscalingSafeguard
        );

        scaledRibbonGroupsMLR = scaledRibbonGroupsMLR.map((group) => {
          const fullyScaledGroup = responsiveScaledRibbonGroupsMLR.find(
            (responsiveGroup) => group.id === responsiveGroup.id
          );

          if (fullyScaledGroup && fullyScaledGroup.hideGroupLabel) {
            group.controls =
              group.controls &&
              group.controls[group.controls.length - 1].id !==
                fullyScaledGroup.controls[0].id
                ? group.controls.concat(fullyScaledGroup.controls)
                : group.controls;
            if (fullyScaledGroup.responsiveLayout) {
              group.responsiveLayout = fullyScaledGroup.responsiveLayout;
            }
          }

          return group;
        });
      }

      return {
        id,
        type: "Multiline",
        ribbonGroupDefinitions,
        scalingSteps: combinedScalingSteps,
        appliedScalingStepCount,
        minimumScalingStepsToApply,
        scaledRibbonGroups: scaledRibbonGroupsMLR,
        initialScaledRibbonGroups: scaledRibbonGroupsMLR,
        controlRendererFunction,
        controlInMenuRendererFunction,
      };
  }
}

/**
 * If no scaling steps are provided, the default Ribbon scaling applies.
 * The expected behavior is to scale down each control group, starting from the last
 * control group. To scale down a control group, we move each control in a control group,
 * except the first control into an overflow well.
 * @param controlGroups The control groups for which to generate scaling steps.
 */
export function getDefaultScalingSteps(
  controlGroups: RibbonControlGroupDefinitionSet
): RibbonScalingStep[] {
  switch (controlGroups.type) {
    case "SingleLine":
      // Flatten the control group definitions into a single list of controls, then reverse it.
      return controlGroups.groups
        .reduce(
          (accumulator, group) => [
            ...accumulator,
            ...group.controls.slice(1).map((control) => {
              return {
                type: "MoveToOverflow",
                controlId: control.id,
              } as RibbonScalingStep;
            }),
          ],
          [] as RibbonScalingStep[]
        )
        .reverse();
    case "Multiline":
      return [];
  }
}

/**
 * return a group with a single flyout with the rest of the controls inside
 */
function convertGroupToFlyout(
  controlGroup: RibbonControlGroupDefinitionMLR,
  controls: RibbonControlProps[],
  controlRendererFunction: ControlRendererFunction,
  controlInMenuRendererFunction: ControlInMenuRendererFunction,
  droppedLabels?: DroppedLabelsDict
): RibbonControlProps[] {
  const alteredControls = controls.map((control) => {
    switch (control.type) {
      case "AppButtonProps":
      case "AppToggleButtonProps":
      case "AppFlyoutAnchorProps":
      case "AppSplitButtonProps":
      case "AppComboBoxProps":
      case "AppSpinButtonProps":
      case "AppCheckboxProps":
      case "AppFontPickerProps":
        let controlProps: RibbonControlProps = {
          ...control,
          keytipProps: control.keytipProps &&
            controlGroup.commonGroupFlyout!.keytipProps && {
              ...control.keytipProps!,
              keySequences:
                controlGroup.commonGroupFlyout!.keytipProps!.keySequences.concat(
                  control.keytipProps!.keySequences.slice(1)
                ),
            },
        };
        controlProps = droppedLabels?.[control.id]
          ? dropLabelFromControlProps(controlProps)
          : controlProps;
        return controlProps;
      default:
        return control;
    }
  });

  const ribbonControlGroupMLRProps: RibbonControlGroupProps = {
    type: "Multiline",
    animate: false,
    controls: alteredControls,
    id: controlGroup.id,
    controlRendererFunction,
    controlInMenuRendererFunction,
    groupLabel: controlGroup.title,
    layout: controlGroup.layouts ? controlGroup.layouts[0] : undefined,
  };

  const focusZoneProps = { direction: FocusZoneDirection.horizontal };
  const flyoutAnchorProps: AppFlyoutAnchorProps = {
    ...controlGroup.commonGroupFlyout!,
    menuDefinition: {
      focusZoneProps: { ...focusZoneProps },
      sections: [{ controls: [ribbonControlGroupMLRProps] }],
    },
    menuClassNames: getMLRControlGroupMenuClassNames(),
  };

  return [flyoutAnchorProps];
}

export const cachedRibbonScalingStateSchemaVersion = 1;
const localStorageScalingId = "cachedRibbonScalingState";
let cachedScalingState: CachedRibbonScalingState | undefined;
let currentWindowMode: WindowMode | undefined;

/**
 * Returns the number of scaling steps that should be applied to the ribbon
 * based on the values cached in the user's localStorage
 *
 * @param mode The current ribbon mode, either 'SingleLine' or 'Multiline'
 * @param tabId The id of the current tab
 * @param reservedSpace How much space is reserved for non-Ribbon elements
 * @returns The number of scaling steps to apply, or undefined if there isn't a corresponding value in the cache
 */
export function getCachedInitialScalingStepsToApply(
  mode: RibbonMode,
  tabId: string,
  reservedSpace: number,
  onError?: (message: string) => void
): number | undefined {
  try {
    // Get the cached scaling state from localStorage if we don't already have it
    if (!cachedScalingState) {
      const localStorageContent = JSON.parse(
        localStorage.getItem(localStorageScalingId)!
      );
      // Ensure that the content received from localStorage is of the type we expect
      assertIsCachedScalingState(localStorageContent);
      cachedScalingState = localStorageContent as CachedRibbonScalingState;
    }
    currentWindowMode = getWindowMode();
    return cachedScalingState[currentWindowMode]?.windowWidth ===
      window.innerWidth - reservedSpace
      ? getCachedStepCountFromState(mode, tabId)
      : undefined;
  } catch (e: any) {
    onError?.(e);
    return undefined;
  }
}

/**
 * Adds a scaling step value to the cache. This updates the corresponding map within cachedScalingState,
 * and updates localStorage if applicable
 *
 * @param mode The current ribbon mode, either 'SingleLine' or 'Multiline'
 * @param tabId The id of the current tab
 * @param scalingStepsApplied The number of scaling steps that were just rendered. This is the value being placed in the cache
 * @param reservedSpace How much space is reserved for non-Ribbon elements
 *
 */
export function addScalingStepsToCache(
  mode: RibbonMode,
  tabId: string,
  scalingStepsApplied: number,
  reservedSpace: number,
  onError?: (message: string) => void
) {
  const currentWindowWidth: number = window.innerWidth - reservedSpace;

  currentWindowMode = getWindowMode();
  // If the cached data is incomplete or the window width has changed since out last cache, we need to build a new object
  if (
    !cachedScalingState ||
    cachedScalingState[currentWindowMode]?.windowWidth !== currentWindowWidth
  ) {
    cachedScalingState = {
      // If we simply don't have data for the current windowMode, keep what has already been cached for the next orientation change
      ...(!cachedScalingState?.[currentWindowMode]
        ? cachedScalingState
        : undefined),
      [currentWindowMode]: {
        windowWidth: currentWindowWidth,
        version: cachedRibbonScalingStateSchemaVersion,
      },
    };
  }

  // If this isn't new information, there's no need to update localStorage
  if (getCachedStepCountFromState(mode, tabId) === scalingStepsApplied) {
    return;
  }
  setCachedStepCountToState(mode, tabId, scalingStepsApplied);

  try {
    // put the new object in localStorage
    localStorage.setItem(
      localStorageScalingId,
      JSON.stringify(cachedScalingState)
    );
  } catch (e: any) {
    onError?.(e);
  }
}

/**
 * Returns the number of scaling steps that should be applied based on the current value in cachedScalingState
 *
 * @param mode The current ribbon mode, either 'SingleLine' or 'Multiline'
 * @param tabId The id of the current tab
 * @returns The number of scaling steps to apply, or undefined if there isn't a corresponding value in cachedScalingState
 */
function getCachedStepCountFromState(
  mode: RibbonMode,
  tabId: string
): number | undefined {
  if (!cachedScalingState || !currentWindowMode) {
    return undefined;
  }

  if (mode === "SingleLine") {
    return cachedScalingState[currentWindowMode]?.slrScalingState?.[tabId];
  }
  return cachedScalingState[currentWindowMode]?.mlrScalingState?.[tabId];
}

/**
 * Updates cachedScalingState with up-to-date scaling steps applied
 *
 * @param mode The current ribbon mode, either 'SingleLine' or 'Multiline'
 * @param tabId The id of the current tab
 * @param scalingStepsApplied The number of scaling steps that were just rendered. This is the value being placed in the cache
 */
function setCachedStepCountToState(
  mode: RibbonMode,
  tabId: string,
  scalingStepsApplied: number
) {
  if (!cachedScalingState || !currentWindowMode) {
    return;
  }
  const currentCachedScalingData: RibbonScalingData =
    cachedScalingState[currentWindowMode];

  if (mode === "SingleLine") {
    currentCachedScalingData.slrScalingState = {
      ...currentCachedScalingData.slrScalingState,
      [tabId]: scalingStepsApplied,
    };
  } else {
    currentCachedScalingData.mlrScalingState = {
      ...currentCachedScalingData.mlrScalingState,
      [tabId]: scalingStepsApplied,
    };
  }

  cachedScalingState = {
    ...cachedScalingState,
    [currentWindowMode]: currentCachedScalingData,
  };
}

/**
 * Asserts that the given param is of type CachedRibbonScalingState. This will help us invalidate the cache when it is
 * incorrect or out of date, and give us confidence that the value from cache is of the type we expect
 *
 * @param obj The value read and parsed from the cache. It is expected to be of type CachedRibbonScalingState
 */

export function assertIsCachedScalingState(
  obj: unknown
): asserts obj is CachedRibbonScalingState {
  assert(
    typeof obj === "object" && obj !== null,
    "Cached scaling state is not an object"
  );
  const cachedScalingState = obj as CachedRibbonScalingState;
  for (const key in cachedScalingState) {
    assert(
      key === "Landscape" || key === "Portrait" || key === "DL" || key === "DP",
      `Invalid WindowMode key ${key}`
    );
    const scalingStateData = cachedScalingState[key] as RibbonScalingData;
    assert(
      scalingStateData.version === cachedRibbonScalingStateSchemaVersion,
      `Cached scaling state unexpected version ${scalingStateData.version}`
    );
    assert(
      scalingStateData.windowWidth > 0,
      `Cached scaling state unexpected windowWidth ${scalingStateData.windowWidth}`
    );
  }
}
